import React, { useState, useEffect } from 'react';
import { ArrowLeft, Search, Filter, Calendar, Users, DollarSign, Clock, X, Send, Trash2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { mockMinisterios } from '@/mocks';
import { useFinancasStore } from '@/stores/financas.store';

interface SolicitacaoMinisterial {
  id: string;
  ministerio: string;
  solicitante: string;
  descricao: string;
  valor: number;
  dataSolicitacao: string;
  dataLimite?: string;
  prioridade: 'baixa' | 'media' | 'alta' | 'urgente';
  status: 'pendente' | 'aprovada' | 'rejeitada' | 'em_analise';
  justificativa: string;
  observacoes?: string;
  tipo?: 'solicitacao' | 'entrada' | 'prestacao' | 'transferencia';
}

interface EnviarSaldoModalProps {
  onClose: () => void;
  onEnviar: (ministerioId: string, valor: number, frequencia: 'unico' | 'mensal', dataEnvio?: string) => void;
}

const EnviarSaldoModal: React.FC<EnviarSaldoModalProps> = ({ onClose, onEnviar }) => {
  const [ministerioSelecionado, setMinisterioSelecionado] = useState('');
  const [valor, setValor] = useState('');
  const [frequencia, setFrequencia] = useState<'unico' | 'mensal'>('unico');
  const [dataEnvio, setDataEnvio] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (ministerioSelecionado && valor) {
      onEnviar(ministerioSelecionado, parseFloat(valor), frequencia, frequencia === 'mensal' ? dataEnvio : undefined);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-card dark:bg-card border border-border dark:border-border p-6 rounded-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-card-foreground dark:text-card-foreground">Enviar Saldo para Ministério</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="ministerio" className="text-card-foreground dark:text-card-foreground">Ministério</Label>
            <Select value={ministerioSelecionado} onValueChange={setMinisterioSelecionado}>
              <SelectTrigger className="bg-background dark:bg-background border-border dark:border-border text-foreground dark:text-foreground">
                <SelectValue placeholder="Selecione um ministério" />
              </SelectTrigger>
              <SelectContent className="bg-background dark:bg-background border-border dark:border-border">
                {mockMinisterios.filter(m => m.ativo).map((ministerio) => (
                  <SelectItem key={ministerio.id} value={ministerio.id} className="text-foreground dark:text-foreground hover:bg-muted dark:hover:bg-muted">
                    {ministerio.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="valor" className="text-card-foreground dark:text-card-foreground">Valor (R$)</Label>
            <Input
              id="valor"
              type="number"
              step="0.01"
              min="0"
              value={valor}
              onChange={(e) => setValor(e.target.value)}
              className="bg-background dark:bg-background border-border dark:border-border text-foreground dark:text-foreground"
              placeholder="0,00"
              required
            />
          </div>

          <div>
            <Label htmlFor="frequencia" className="text-card-foreground dark:text-card-foreground">Tipo de Envio</Label>
            <Select value={frequencia} onValueChange={(value) => setFrequencia(value as 'unico' | 'mensal')}>
              <SelectTrigger className="bg-background dark:bg-background border-border dark:border-border text-foreground dark:text-foreground">
                <SelectValue placeholder="Selecione o tipo de envio" />
              </SelectTrigger>
              <SelectContent className="bg-background dark:bg-background border-border dark:border-border">
                <SelectItem value="unico" className="text-foreground dark:text-foreground hover:bg-muted dark:hover:bg-muted">
                  Envio Único
                </SelectItem>
                <SelectItem value="mensal" className="text-foreground dark:text-foreground hover:bg-muted dark:hover:bg-muted">
                  Envio Mensal
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {frequencia === 'mensal' && (
            <div>
              <Label htmlFor="dataEnvio" className="text-card-foreground dark:text-card-foreground">Data do Envio Mensal</Label>
              <Input
                id="dataEnvio"
                type="date"
                value={dataEnvio}
                onChange={(e) => setDataEnvio(e.target.value)}
                className="bg-background dark:bg-background border-border dark:border-border text-foreground dark:text-foreground"
                required={frequencia === 'mensal'}
              />
              <p className="text-sm text-muted-foreground dark:text-muted-foreground mt-1">
                O saldo será enviado automaticamente todo mês nesta data
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" className="flex-1 bg-green-600 hover:bg-green-700">
              Enviar Saldo
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

const SolicitacoesMinistriaisPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { solicitacoes: solicitacoesStore, addSolicitacao } = useFinancasStore();

  const [showEnviarSaldoModal, setShowEnviarSaldoModal] = useState(false);
  const [filtros, setFiltros] = useState({
    ministerio: '',
    solicitante: '',
    status: '',
    prioridade: '',
    dataInicio: '',
    dataFim: ''
  });

  // Estado para as solicitações
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoMinisterial[]>([]);

  // Inicializar solicitações
  useEffect(() => {
    const solicitacoesIniciais: SolicitacaoMinisterial[] = [
      // Dados mockados para exemplo
      {
        id: '1',
        ministerio: 'louvor',
        solicitante: 'João Silva',
        descricao: 'Compra de microfones sem fio',
        valor: 2500.00,
        dataSolicitacao: '2024-11-01',
        dataLimite: '2024-12-15',
        prioridade: 'alta',
        status: 'aprovada',
        justificativa: 'Equipamentos atuais com defeito',
        observacoes: 'Urgente para cultos especiais',
        tipo: 'solicitacao'
      },
      {
        id: '2',
        ministerio: 'jovens',
        solicitante: 'Maria Santos',
        descricao: 'Material para retiro de jovens',
        valor: 1800.00,
        dataSolicitacao: '2024-11-10',
        dataLimite: '2024-11-30',
        prioridade: 'media',
        status: 'pendente',
        justificativa: 'Retiro anual dos jovens',
        observacoes: 'Inclui transporte e alimentação',
        tipo: 'solicitacao'
      },
      // Converter solicitações do store
       ...solicitacoesStore.map(sol => ({
         id: sol.id,
         ministerio: sol.ministerioId,
         solicitante: sol.solicitante || 'Não informado',
         descricao: sol.titulo,
         valor: sol.valor,
         dataSolicitacao: sol.data,
         prioridade: sol.prioridade || 'media' as const,
         status: (() => {
           // Mapear status do store para status da interface
           switch (sol.status) {
             case 'aprovado': return 'aprovada' as const;
             case 'rejeitado': return 'rejeitada' as const;
             case 'em_analise': return 'em_analise' as const;
             default: return 'pendente' as const;
           }
         })(),
         justificativa: sol.motivo,
         tipo: 'solicitacao' as const
       }))
    ];
    setSolicitacoes(solicitacoesIniciais);
  }, [solicitacoesStore]);

  // Detectar ação vinda dos ministérios
  useEffect(() => {
    if (location.state) {
      const { ministerioId, action } = location.state as { ministerioId: string; action: string };
      
      switch (action) {
        case 'solicitar':
          // Navegar para página de cadastro com ministério pré-selecionado
          navigate(`/financas/administracao/solicitacoes-ministeriais/cadastrar?ministerio=${ministerioId}`);
          break;
        case 'entrada':
          // Mostrar apenas entradas informativas
          setFiltros(prev => ({ ...prev, ministerio: ministerioId, status: 'aprovada' }));
          break;
        case 'prestacao':
          // Filtrar por prestações de contas
          setFiltros(prev => ({ ...prev, ministerio: ministerioId }));
          break;
        case 'transferir':
          // Filtrar por transferências
          setFiltros(prev => ({ ...prev, ministerio: ministerioId }));
          break;
      }
      
      // Limpar o state para evitar reprocessamento
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const handleDeleteSolicitacao = (id: string) => {
    if (confirm('Tem certeza que deseja deletar esta solicitação?')) {
      // Remover da lista de solicitações
      setSolicitacoes(prev => prev.filter(sol => sol.id !== id));
      alert('Solicitação deletada com sucesso!');
    }
  };

  const handleEnviarSaldo = (ministerioId: string, valor: number, frequencia: 'unico' | 'mensal', dataEnvio?: string) => {
    // Criar uma solicitação de entrada para o ministério com aprovação automática
    addSolicitacao({
      ministerioId,
      titulo: `Envio de Saldo - ${frequencia === 'mensal' ? 'Mensal' : 'Único'}`,
      motivo: `Transferência de saldo ${frequencia === 'mensal' ? 'mensal' : 'única'} para o ministério${dataEnvio ? ` (todo dia ${new Date(dataEnvio).getDate()} de cada mês)` : ''}`,
      valor,
      solicitante: 'Administração',
      prioridade: 'media',
      autoApprove: true // Aprovação automática para envios de saldo
    });
    
    const tipoEnvio = frequencia === 'mensal' ? 'mensal' : 'único';
    const mensagemData = frequencia === 'mensal' && dataEnvio 
      ? ` (todo dia ${new Date(dataEnvio).getDate()} de cada mês)` 
      : '';
    
    alert(`Saldo de R$ ${valor.toFixed(2)} configurado para envio ${tipoEnvio}${mensagemData} com sucesso!`);
  };

  // Aplicar filtros às solicitações
  const solicitacoesFiltradas = solicitacoes.filter(solicitacao => {
    // Filtro por ministério
    if (filtros.ministerio && solicitacao.ministerio !== filtros.ministerio) {
      return false;
    }
    
    // Filtro por solicitante
    if (filtros.solicitante && !solicitacao.solicitante.toLowerCase().includes(filtros.solicitante.toLowerCase())) {
      return false;
    }
    
    // Filtro por status
    if (filtros.status && solicitacao.status !== filtros.status) {
      return false;
    }
    
    // Filtro por prioridade
    if (filtros.prioridade && solicitacao.prioridade !== filtros.prioridade) {
      return false;
    }
    
    // Filtro por data de início
    if (filtros.dataInicio) {
      const dataInicio = new Date(filtros.dataInicio);
      const dataSolicitacao = new Date(solicitacao.dataSolicitacao);
      if (dataSolicitacao < dataInicio) {
        return false;
      }
    }
    
    // Filtro por data de fim
    if (filtros.dataFim) {
      const dataFim = new Date(filtros.dataFim);
      const dataSolicitacao = new Date(solicitacao.dataSolicitacao);
      if (dataSolicitacao > dataFim) {
        return false;
      }
    }
    
    return true;
  });

  const totalSolicitado = solicitacoesFiltradas.reduce((acc, sol) => acc + sol.valor, 0);
  const solicitacoesPendentes = solicitacoesFiltradas.filter(s => s.status === 'pendente').length;
  const solicitacoesAnalise = solicitacoesFiltradas.filter(s => s.status === 'em_analise').length;
  const solicitacoesAprovadas = solicitacoesFiltradas.filter(s => s.status === 'aprovada').length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pendente': return 'bg-gray-500';
      case 'em_analise': return 'bg-yellow-500';
      case 'aprovada': 
      case 'aprovado': return 'bg-green-500';
      case 'rejeitada': 
      case 'rejeitado': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pendente': return 'Pendente';
      case 'em_analise': return 'Em Análise';
      case 'aprovada': 
      case 'aprovado': return 'Aprovado';
      case 'rejeitada': 
      case 'rejeitado': return 'Rejeitado';
      default: return status;
    }
  };

  const handleStatusClick = (id: string) => {
    setSolicitacoes(prev => prev.map(solicitacao => {
      if (solicitacao.id === id) {
        const statusOrder: SolicitacaoMinisterial['status'][] = ['pendente', 'em_analise', 'aprovada', 'rejeitada'];
        const currentIndex = statusOrder.indexOf(solicitacao.status);
        const nextIndex = (currentIndex + 1) % statusOrder.length;
        return { ...solicitacao, status: statusOrder[nextIndex] };
      }
      return solicitacao;
    }));
  };

  const getPrioridadeColor = (prioridade: string) => {
    switch (prioridade) {
      case 'baixa': return 'bg-blue-500';
      case 'media': return 'bg-yellow-500';
      case 'alta': return 'bg-orange-500';
      case 'urgente': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getPrioridadeText = (prioridade: string) => {
    switch (prioridade) {
      case 'baixa': return 'Baixa';
      case 'media': return 'Média';
      case 'alta': return 'Alta';
      case 'urgente': return 'Urgente';
      default: return prioridade;
    }
  };

  const getMinisterioText = (ministerio: string) => {
    // Primeiro tenta encontrar pelo ID
    const ministerioEncontrado = mockMinisterios.find(m => m.id === ministerio);
    if (ministerioEncontrado) {
      return ministerioEncontrado.nome;
    }
    
    // Fallback para o mapeamento antigo (para compatibilidade)
    switch (ministerio) {
      case 'louvor': return 'Louvor';
      case 'jovens': return 'Jovens';
      case 'criancas': return 'Crianças';
      case 'acao_social': return 'Ação Social';
      case 'intercessao': return 'Intercessão';
      case 'recepcao': return 'Recepção';
      default: return ministerio;
    }
  };

  return (
    <div className="min-h-screen bg-background dark:bg-background text-foreground dark:text-foreground p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/financas/administracao')}
              className="mr-4"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold">Solicitações Ministeriais</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowEnviarSaldoModal(true)} className="bg-green-600 hover:bg-green-700">
              <Send className="h-4 w-4 mr-2" />
              Enviar Saldo
            </Button>
          </div>
        </div>

        {/* Resumo */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">Total Solicitado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="h-4 w-4 text-blue-500 mr-2" />
                <span className="text-2xl font-bold text-blue-500">
                  R$ {totalSolicitado.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">Pendentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Clock className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-2xl font-bold text-gray-500">{solicitacoesPendentes}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">Em Análise</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="h-4 w-4 text-yellow-500 mr-2" />
                <span className="text-2xl font-bold text-yellow-500">{solicitacoesAnalise}</span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card dark:bg-card border-border dark:border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground">Aprovadas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="h-4 w-4 text-green-500 mr-2" />
                <span className="text-2xl font-bold text-green-500">{solicitacoesAprovadas}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filtros e Tabela Unificados */}
        <Card className="bg-card dark:bg-card border-border dark:border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-card-foreground dark:text-card-foreground">
              <Filter className="h-4 w-4" />
              Filtros e Solicitações
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filtros */}
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
              <div>
                <Label htmlFor="filtro-ministerio">Ministério</Label>
                <Select value={filtros.ministerio} onValueChange={(value) => setFiltros({...filtros, ministerio: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os ministérios" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os ministérios</SelectItem>
                    {mockMinisterios.map((ministerio) => (
                      <SelectItem key={ministerio.id} value={ministerio.id}>
                        {ministerio.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filtro-solicitante">Solicitante</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="filtro-solicitante"
                    placeholder="Buscar solicitante..."
                    value={filtros.solicitante}
                    onChange={(e) => setFiltros({...filtros, solicitante: e.target.value})}
                    className="pl-10 bg-background dark:bg-background border-border dark:border-border"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="filtro-status">Status</Label>
                <Select value={filtros.status} onValueChange={(value) => setFiltros({...filtros, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos os status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="em_analise">Em Análise</SelectItem>
                    <SelectItem value="aprovada">Aprovada</SelectItem>
                    <SelectItem value="rejeitada">Rejeitada</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filtro-prioridade">Prioridade</Label>
                <Select value={filtros.prioridade} onValueChange={(value) => setFiltros({...filtros, prioridade: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todas as prioridades" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as prioridades</SelectItem>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="filtro-data-inicio">Data Início</Label>
                <Input
                  id="filtro-data-inicio"
                  type="date"
                  value={filtros.dataInicio}
                  onChange={(e) => setFiltros({...filtros, dataInicio: e.target.value})}
                  className="bg-background dark:bg-background border-border dark:border-border"
                />
              </div>

              <div>
                <Label htmlFor="filtro-data-fim">Data Fim</Label>
                <Input
                  id="filtro-data-fim"
                  type="date"
                  value={filtros.dataFim}
                  onChange={(e) => setFiltros({...filtros, dataFim: e.target.value})}
                  className="bg-background dark:bg-background border-border dark:border-border"
                />
              </div>
            </div>

            {/* Lista de Solicitações */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border dark:border-border">
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Ministério</th>
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Solicitante</th>
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Descrição</th>
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Valor</th>
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Data</th>
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Prioridade</th>
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Status</th>
                    <th className="text-left p-2 text-muted-foreground dark:text-muted-foreground">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {solicitacoesFiltradas.map((solicitacao) => (
                    <tr key={solicitacao.id} className="border-b border-border dark:border-border">
                      <td className="p-2 text-card-foreground dark:text-card-foreground">{getMinisterioText(solicitacao.ministerio)}</td>
                      <td className="p-2 text-card-foreground dark:text-card-foreground">{solicitacao.solicitante}</td>
                      <td className="p-2 text-card-foreground dark:text-card-foreground">{solicitacao.descricao}</td>
                      <td className="p-2 text-card-foreground dark:text-card-foreground">R$ {solicitacao.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</td>
                      <td className="p-2 text-card-foreground dark:text-card-foreground">{new Date(solicitacao.dataSolicitacao).toLocaleDateString('pt-BR')}</td>
                      <td className="p-2">
                        <Badge className={`${getPrioridadeColor(solicitacao.prioridade)} text-white`}>
                          {getPrioridadeText(solicitacao.prioridade)}
                        </Badge>
                      </td>
                      <td className="p-2">
                        <Badge 
                          className={`${getStatusColor(solicitacao.status)} text-white cursor-pointer relative group`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleStatusClick(solicitacao.id);
                          }}
                          title="Clique para mudar status"
                        >
                          {getStatusText(solicitacao.status)}
                          <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 text-xs text-white bg-gray-900 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-50">
                            Clique para mudar status
                          </span>
                        </Badge>
                      </td>
                      <td className="p-2">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteSolicitacao(solicitacao.id)}
                            className="text-red-400 hover:text-red-300"
                            title="Deletar solicitação"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Modal de Enviar Saldo */}
        {showEnviarSaldoModal && (
          <EnviarSaldoModal
            onClose={() => setShowEnviarSaldoModal(false)}
            onEnviar={handleEnviarSaldo}
          />
        )}
      </div>
    </div>
  );
};

export default SolicitacoesMinistriaisPage;
export { SolicitacoesMinistriaisPage };